import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { LocalStorageService } from '../services/localstorage.service';
import {
  DashboardService,
  FrotaRetornoItem,
  PrecoAlertaDestaque,
} from './dashboard.service';

/** Filtro de direção aplicado na lista de alertas. */
export type FiltroDirecao = 'todos' | 'subiu' | 'desceu';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  // ============================================================
  // Constantes
  // ============================================================

  /** Código do combustível Diesel S10 (parâmetro do endpoint `getPrecoBomba`). */
  private static readonly COD_DIESEL_S10 = 9;

  readonly itensPorPagina = 6;
  readonly idPaginacao = 'alertasPrecoPaginacao';

  // ============================================================
  // KPIs (cards)
  // ============================================================

  economiaPotencial = 0;
  totalAlertasPrecoBomba = 0;

  // ============================================================
  // Dados brutos da API
  // ============================================================

  data?: FrotaRetornoItem[];
  loading = true;

  // ============================================================
  // Busca / Filtro / Paginação
  // ============================================================

  termoBusca = '';
  filtroDirecao: FiltroDirecao = 'todos';
  paginaAtual = 1;

  // ============================================================
  // Cache derivado de `data`
  // ============================================================

  private alertasPrecoDetalhe: PrecoAlertaDestaque[] = [];

  // ============================================================
  // Cleanup
  // ============================================================

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly router: Router,
    private readonly localStoreService: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.carregarEconomiaPotencial();
    this.carregarPrecosBomba(DashboardComponent.COD_DIESEL_S10);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================
  // Handlers de UI
  // ============================================================

  trackByChave(_index: number, alerta: PrecoAlertaDestaque): string {
    return alerta.chave;
  }

  onBuscaChange(): void {
    this.paginaAtual = 1;
  }

  selecionarFiltroDirecao(filtro: FiltroDirecao): void {
    if (this.filtroDirecao === filtro) {
      return;
    }
    this.filtroDirecao = filtro;
    this.paginaAtual = 1;
  }

  /**
   * Limpa busca por texto **e** filtro de direção, voltando à lista
   * completa. Usado pelo botão "limpar busca" e pelo empty state.
   */
  limparFiltros(): void {
    this.termoBusca = '';
    this.filtroDirecao = 'todos';
    this.paginaAtual = 1;
  }

  /**
   * Volta uma página no histórico do navegador. Equivalente ao botão
   * "Voltar" do navegador, mas exposto como CTA na barra de cabeçalho
   * da página.
   */
  voltarHome(): void {
    //https://homolog.portalctf.com.br/Portal/ListaPrecos/PesquisarPrecosBomba?Formato=Relatorio&cmdName=2383&userGuid=2ce5a07d-25db-429c-b7ae-51d7b5768042
    //window.history.back();

    const userGuid = this.localStoreService.getItem('guid');

    window.location.href =
      '../Portal/ListaPrecos/PesquisarPrecosBomba?Formato=Relatorio&cmdName=2383&userGuid=' +
      userGuid;
  }

  // ============================================================
  // Getters - dados derivados
  // ============================================================

  get alertasFiltrados(): PrecoAlertaDestaque[] {
    const termo = this.termoBusca.trim().toLowerCase();
    const direcao = this.filtroDirecao;
    return this.alertasPrecoDetalhe.filter((a) => {
      if (direcao !== 'todos' && a.direcao !== direcao) {
        return false;
      }
      if (termo && !a.tituloLocal.toLowerCase().includes(termo)) {
        return false;
      }
      return true;
    });
  }

  get totalAlertas(): number {
    return this.alertasPrecoDetalhe.length;
  }

  get totalFiltrados(): number {
    return this.alertasFiltrados.length;
  }

  get totalSubiram(): number {
    return this.alertasPrecoDetalhe.filter((a) => a.direcao === 'subiu').length;
  }

  get totalDesceram(): number {
    return this.alertasPrecoDetalhe.filter((a) => a.direcao === 'desceu')
      .length;
  }

  /** `true` quando o usuário aplicou busca de texto ou chip diferente de Todos. */
  get temFiltrosAtivos(): boolean {
    return this.termoBusca.trim().length > 0 || this.filtroDirecao !== 'todos';
  }

  // ============================================================
  // Getters - estados de UI (empty / paginação)
  // ============================================================

  get mostrarEmptySemDados(): boolean {
    return this.totalFiltrados === 0 && this.totalAlertas === 0;
  }

  get mostrarEmptyFiltro(): boolean {
    return this.totalFiltrados === 0 && this.totalAlertas > 0;
  }

  get mostrarPaginacao(): boolean {
    return this.totalFiltrados > this.itensPorPagina;
  }

  // ============================================================
  // Privados - chamadas ao service
  // ============================================================

  private carregarEconomiaPotencial(): void {
    this.dashboardService
      .getEconomiaPotencialPrecoBomba()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (valor: number) => (this.economiaPotencial = valor),
        error: () => (this.economiaPotencial = 0),
      });
  }

  private carregarPrecosBomba(combustivel: number): void {
    this.dashboardService
      .getPrecoBomba(combustivel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (itens: FrotaRetornoItem[]) => this.handleItensRecebidos(itens),
        error: () => this.handleErroFetch(),
      });
  }

  private handleItensRecebidos(itens: FrotaRetornoItem[]): void {
    this.data = itens;
    this.alertasPrecoDetalhe = this.dashboardService.mapItensToAlertas(itens);
    this.totalAlertasPrecoBomba = this.alertasPrecoDetalhe.length;
    this.loading = false;
  }

  private handleErroFetch(): void {
    this.data = [];
    this.totalAlertasPrecoBomba = 0;
    this.alertasPrecoDetalhe = [];
    this.loading = false;
  }
}
